import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useEffect, useState } from "react";
import { baseapi } from "../Environmentalurl";
import { ApplicationType, entity_id, locationGeo, Platform } from "../Utils/Const";
import useIPAddress from "../Utils/useIPAddress";

export default function AmountCard({
  amount,
  setAmount,
  isValidAmount,
  toMobile,
  setToMobile,
  onProceed,
}) {
  const [kycInfo, setKycInfo] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState("");
   const ip = useIPAddress();

  // ✅ Auto-call when 10 digits entered
  useEffect(() => {
    if (toMobile.length !== 10) {
      setKycInfo(null);
      setKycError("");
      return;
    }

    const fetchKYC = async () => {
      setKycLoading(true);
      setKycError("");
      setKycInfo(null);
      try {
        // ✅ Get token — check all possible keys
        const token =
          localStorage.getItem("token") ||
          localStorage.getItem("authToken") ||
          localStorage.getItem("accessToken") ||
          localStorage.getItem("jwt") ||
          sessionStorage.getItem("token") ||
          sessionStorage.getItem("authToken") ||
          sessionStorage.getItem("accessToken") ||
          sessionStorage.getItem("jwt") ||
          "";

        console.log("Using token:", token); // 🔍 check in browser console

        const response = await fetch(
          `${baseapi}/JanaadhaareWalletGetKYCStatus`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              ReqType: "Jan_Aadhaar_eWallet_GetKYCStatus",
              SSO_ID: "-",
              Mobilenumber: toMobile,
              MachineID: ip,
              Platform: Platform,
              ApplicationType: ApplicationType,
              GeoLocation: locationGeo,
              EntityID: entity_id,
              SessionID: sessionStorage.getItem("SessionID"),
            }),
          },
        );

        const data = await response.json();
        console.log("KYC Response:", data);

        if (data?.Response_Code === 4000 || data?.Response_Code === "4000") {
          setKycInfo(data);
        } else {
          setKycError(data?.Message || "Wallet not found.");
        }
      } catch (err) {
        console.error("KYC fetch error:", err);
        setKycError("Unable to verify. Please try again.");
      } finally {
        setKycLoading(false);
      }
    };

    fetchKYC();
  }, [toMobile]);

  return (
    <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #e6e9ef" }}>
      {/* ===== TO MOBILE NUMBER ===== */}
      <Typography fontSize={11} fontWeight={600} color="#7a7f87" mb={1}>
        ENTER WALLET / MOBILE NUMBER
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          border: `1px solid ${
            kycError ? "#f44336" : kycInfo ? "#1dbf73" : "#e6e9ef"
          }`,
          borderRadius: 2,
          px: 2,
          py: 1.5,
          transition: "border-color 0.3s",
        }}
      >
        <Typography color="#7a7f87" fontWeight={600}>
          +91
        </Typography>
        <TextField
          variant="standard"
          value={toMobile}
          autoComplete="off"
          onChange={(e) => setToMobile(e.target.value)}
          InputProps={{ disableUnderline: true }}
          sx={{ ml: 2, flex: 1 }}
        />
        {/* ✅ Icon inside input */}
        {toMobile.length === 10 && (
          <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
            {kycLoading ? (
              <CircularProgress size={18} sx={{ color: "#50d15d" }} />
            ) : kycInfo ? (
              <CheckCircleIcon sx={{ color: "#1dbf73", fontSize: 20 }} />
            ) : kycError ? (
              <ErrorOutlineIcon sx={{ color: "#f44336", fontSize: 20 }} />
            ) : null}
          </Box>
        )}
      </Box>

      {/* ✅ Show name after KYC success */}
      {kycInfo && (
        <Box
          sx={{
            mt: 1.5,
            px: 2,
            py: 1.2,
            borderRadius: 2,
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <PersonIcon sx={{ color: "#1dbf73", fontSize: 26 }} />
          <Box>
            <Typography fontSize={14} fontWeight={700} color="#15803d">
              {kycInfo.Name}
            </Typography>
            <Typography fontSize={11} color="#4ade80" fontWeight={500}>
              {kycInfo.KYC} 
              • {kycInfo.Mobilenumber}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ✅ Show error */}
      {kycError && (
        <Typography
          fontSize={12}
          color="#f44336"
          mt={1}
          display="flex"
          alignItems="center"
          gap={0.5}
        >
          <ErrorOutlineIcon sx={{ fontSize: 14 }} /> {kycError}
        </Typography>
      )}

      {!kycError && !kycInfo && (
        <Typography fontSize={12} color="#9aa0a6" mt={1}>
          Enter a registered wallet number to continue.
        </Typography>
      )}

      {/* ===== AMOUNT ===== */}
      <Box mt={4}>
        <Typography fontSize={11} fontWeight={600} color="#7a7f87" mb={1}>
          ENTER AMOUNT HERE
        </Typography>

        <Box
          sx={{
            border: "1px solid #e6e9ef",
            borderRadius: 2,
            px: 2,
            py: 2,
          }}
        >
          <TextField
            variant="standard"
            value={amount}
            autoComplete="off"
            onChange={(e) => setAmount(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: 22,
                fontWeight: 700,
                color: isValidAmount ? "#000000" : "#c5c9d1",
              },
            }}
            fullWidth
          />
        </Box>
      </Box>

      {/* ===== QUICK AMOUNT CHIPS (LOGIC ADDED ONLY) ===== */}
      <Box display="flex" gap={1.5} mt={3}>
        <Chip label="+ ₹100" onClick={() => setAmount("100")} />
        <Chip label="+ ₹500" onClick={() => setAmount("500")} />
        <Chip label="+ ₹1000" onClick={() => setAmount("1000")} />
      </Box>

      {/* ===== PROCEED BUTTON ===== */}
      <Button
        fullWidth
        disabled={!isValidAmount}
        onClick={onProceed}
        sx={{
          mt: 4,
          py: 1.8,
          borderRadius: 2,
          fontWeight: 600,
          bgcolor: "#50d15d",
          color: "#ffffff",
        }}
      >
        Proceed to Pay →
      </Button>

      {/* ===== FOOTER INFO ===== */}
      <Box display="flex" justifyContent="center" gap={0.5} mt={2}>
        <CheckCircleIcon sx={{ fontSize: 16, color: "#1dbf73" }} />
        <Typography fontSize={12} color="#1dbf73">
          Secure Wallet-to-Wallet Transfer
        </Typography>
      </Box>
    </Paper>
  );
}
